"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { getLocalUserId } from "./client";

export interface UserIdentity {
  user_id: string;
  pseudonym: string;
  is_new: boolean;
  can_reroll: boolean;
}

/**
 * Hook to manage user identity resolution
 * Automatically resolves identity on mount and provides reroll functionality
 */
export function useIdentity() {
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvingRef = useRef(false);
  const identityRef = useRef<UserIdentity | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    identityRef.current = identity;
  }, [identity]);

  /**
   * Resolve identity with the API
   */
  const resolveIdentity = useCallback(async (reroll = false) => {
    // Prevent concurrent resolutions
    if (resolvingRef.current && !reroll) {
      return identityRef.current;
    }
    
    resolvingRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const localUserId = getLocalUserId();
      if (!localUserId) {
        throw new Error("Failed to get local user ID");
      }

      const response = await fetch("/api/identity/resolve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          local_user_id: localUserId,
          reroll,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to resolve identity");
      }

      const data: UserIdentity = await response.json();
      setIdentity(data);
      identityRef.current = data;
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      console.error("Identity resolution error:", err);
      return null;
    } finally {
      setLoading(false);
      resolvingRef.current = false;
    }
  }, []);

  /**
   * Reroll pseudonym (only works if user hasn't published yet)
   */
  const rerollPseudonym = useCallback(async () => {
    if (!identity?.can_reroll) {
      console.warn("Cannot reroll: user has already published posts");
      return null;
    }

    return resolveIdentity(true);
  }, [identity?.can_reroll, resolveIdentity]);

  /**
   * Resolve identity on mount
   */
  useEffect(() => {
    void resolveIdentity();
  }, [resolveIdentity]);

  return {
    identity,
    loading,
    error,
    rerollPseudonym,
    refreshIdentity: () => resolveIdentity(false),
  };
}

/**
 * Standalone function to resolve identity (non-hook)
 * Useful for server actions or one-off resolutions
 */
export async function resolveIdentityOnce(reroll = false): Promise<UserIdentity | null> {
  try {
    const localUserId = getLocalUserId();
    if (!localUserId) {
      throw new Error("Failed to get local user ID");
    }

    const response = await fetch("/api/identity/resolve", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        local_user_id: localUserId,
        reroll,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to resolve identity");
    }

    return await response.json();
  } catch (err) {
    // Detect network errors and provide user-friendly message
    // Fetch throws TypeError when network request fails (offline, DNS error, etc.)
    const isNetworkError = err instanceof TypeError && (
      (typeof navigator !== 'undefined' && !navigator.onLine) || // Browser reports offline
      (err.message && (
        err.message.includes('fetch failed') ||
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.toLowerCase().includes('network')
      ))
    );
    
    if (isNetworkError) {
      throw new Error("Not able to connect. Please check your internet connection and try again.");
    }
    
    console.error("Identity resolution error:", err);
    // Re-throw the error so caller can handle it
    throw err;
  }
}

